import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import dayjs from 'dayjs';
import { LegacyPaginationSchema } from 'types/pagination';
import { z } from 'zod/v4';

import { LegacyAttachedInvoicesSchema } from './schemas/attached-invoices';
import { type ApiError, addResourceNameToQueryResult, useLegacyAxiosInstance } from './utils';

const TaskTypeSchema = z
  .object({
    id: z.coerce.string(),
    type: z.string(),
    attributes: z.object({
      uid: z.string(),
      name: z.string(),
      color: z.string().optional(),
      is_deletable: z.boolean().optional(),
    }),
  })
  .transform(({ id, type, attributes }) => ({ id, type, ...attributes }));

export const AssigneeSchema = z
  .object({
    id: z.coerce.string(),
    type: z.string(),
    attributes: z.object({
      email: z.string(),
      first_name: z.string(),
      last_name: z.string(),
      profile_picture_url: z.string().nullable().optional(),
    }),
  })
  .transform(({ id, type, attributes }) => ({ id, type, ...attributes }));

export type Assignee = z.infer<typeof AssigneeSchema>;

const DebtorSchema = z
  .object({
    id: z.coerce.string(),
    type: z.string(),
    attributes: z.any(), // we should migrate debtor to properly type that
  })
  .transform(({ id, type, attributes }) => ({ id, type, ...attributes }));

export const TaskSchema = z
  .object({
    id: z.coerce.string(),
    type: z.string(),
    attributes: z.object({
      title: z.string(),
      description: z.string().nullable(),
      mandatory: z.boolean(),
      task_type_id: z.coerce.number(),
      task_type: TaskTypeSchema,
      skipped: z.boolean(),
      skipped_at: z.string().transform(dayjs).nullable().optional(),
      completed: z.boolean(),
      ai_call_autogenerated: z.boolean(),
      completed_at: z.string().transform(dayjs).nullable(),
      recovery_plan_id: z.coerce.string().nullable(),
      status: z.string(),
      status_key: z.string(),
      created_at: z.string().transform(dayjs),
      due_date: z.string().transform(dayjs).nullable(),
      due_date_adjusted: z.string().transform(dayjs).nullable().optional(),
      days_postponed: z.number(),
      start_offset_days: z.number().nullable(),
      assignee: AssigneeSchema,
      additional_assignees: z.array(AssigneeSchema).optional().default([]),
      debtor: DebtorSchema.nullable(),
      invoices: LegacyAttachedInvoicesSchema.optional().catch(undefined),
    }),
  })
  .transform(({ id, type, attributes }) => ({ id, type, ...attributes }));

const MetadataSchema = z.object({
  total: z.number(),
  pagination: LegacyPaginationSchema,
});

const TaskListResponseSchema = z.object({
  data: z.array(TaskSchema),
  metadata: MetadataSchema,
});

export type TaskListResponse = z.infer<typeof TaskListResponseSchema>;
export type Task = z.infer<typeof TaskSchema>;

const LoadTasksVariablesSchema = z.object({
  debtor_id: z.string(),
});

export type LoadTasksVariables = z.infer<typeof LoadTasksVariablesSchema>;

const URL = '/tasks';

async function loadTasksQueryFn(
  axiosInstance: Promise<AxiosInstance>,
  variables: LoadTasksVariables,
) {
  const instance = await axiosInstance;
  const { data } = await instance.get(URL, { params: variables });

  return TaskListResponseSchema.parse(data);
}

const emptyTasks: TaskListResponse = {
  data: [],
  metadata: {
    total: 0,
    pagination: { total_objects: 0, page_limit: 0, current_page: 1, last_page: 1 },
  },
};

export function useLoadTasks(
  variables: LoadTasksVariables,
  options: { enabled?: boolean } = { enabled: true },
) {
  const axiosInstance = useLegacyAxiosInstance();

  const queryResult = useQuery<TaskListResponse, ApiError>({
    queryKey: ['tasks', variables],
    queryFn: () => loadTasksQueryFn(axiosInstance, variables),
    placeholderData: data => keepPreviousData(data),
    enabled: options.enabled,
  });

  return addResourceNameToQueryResult<TaskListResponse, unknown, 'tasks', 'PreventUndefined'>(
    'tasks',
    queryResult,
    emptyTasks,
  );
}
